import { DatePipe } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { DocumentDownload, Separation } from 'src/app/shared/models/separation';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SeparationService } from 'src/app/shared/services/separation.service';

@Component({
  selector: 'app-document-download',
  templateUrl: './document-download.component.html',
  styleUrls: ['./document-download.component.scss']
})
export class DocumentDownloadComponent implements OnInit {
  @Input() context: Context;
  @Input() separation: Separation;
  @Input() separationId: number;

  public documentDownload: DocumentDownload;
  public documentDownloadForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public types: string;
  public selectedFile: File | null = null;
  public formData: FormData;
  public documents: DocumentDownload[] = [];

  constructor(
    private separationService: SeparationService,
    private commonService: CommonService,
    private fb: FormBuilder,
    private authService: AuthService,
    private datePipe: DatePipe,
    private httpClient: HttpClient,
  ) { }

  ngOnInit(): void {
    this.context = this.authService.getContextData();
    this.getDocumentDownloads();
    this.setDocumentDownload([]);
    this.separationService.getDocumentType().subscribe(types => (this.types = types));
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0]; 
  }

  public getDocumentDownloads() {
    if (this.separationId) {
      this.separationService.getDocumentDownloads(this.separationId)
        .subscribe(documentDownloads => {
          if (documentDownloads && documentDownloads.length) {
            this.documents = documentDownloads.map(doc => ({
              ...doc,
              genearatedDate: this.formatDate(doc.genearatedDate)
            }));
            this.setDocumentDownload(this.documents);
          } else {
            this.setDocumentDownload([]);
          }
        });
    }
  }
  

  get documentDownloads(): FormArray {
    return this.documentDownloadForm.get("documentDownloads") as FormArray;
  }

  private setDocumentDownload(documentDownloads: DocumentDownload[]) {
    this.documentDownloadForm = this.fb.group({
      documentDownloads: this.fb.array([])
    });
    if (documentDownloads && documentDownloads.length > 0) {
      documentDownloads.forEach((doc) => this.addDocumentDownload(doc));
    } else {
      this.addDocumentDownload();
    }
  }

  private addDocumentDownload(documentDownload?: DocumentDownload) {
    if (this.documentDownloads.controls.some(doc => !doc.value.filePath || !doc.value.type)) {
      this.commonService.setToastr(400, 'Complete the existing row before adding a new one.');
      return;
    }
    const today = new Date();
    const formattedDate = this.datePipe.transform(today, 'yyyy-MM-dd');
    this.documentDownloads.push(this.fb.group({
      id: this.fb.control(documentDownload?.id || null, []),
      date: this.fb.control(documentDownload?.date || formattedDate, [Validators.required]),
      genearatedDate: this.fb.control(documentDownload?.genearatedDate || formattedDate, []),
      filePath: this.fb.control(documentDownload?.filePath || null, []),
      type: this.fb.control(documentDownload?.type || null, [Validators.required]),
      separationId: this.fb.control(documentDownload?.separationId || this.separationId, [Validators.required]),
      employeeName: this.fb.control(documentDownload?.employeeName || null, []),
      disabled: this.fb.control(documentDownload?.disabled || false, [])
    }));
  }

  public onFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.commonService.setToastr(200, `Selected file: ${this.selectedFile.name}`);
    }
  }

  public uploadFile(documentId: number) {
    if (this.selectedFile) {
      const formData = new FormData();
      formData.append("file", this.selectedFile);
      this.separationService.uploadFile(documentId, formData).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            this.getDocumentDownloads();
            setTimeout(() => {
              this.addDocumentDownload();
            }, 500); 
            this.commonService.setToastr(200, response.text);
          } else {
            this.commonService.setToastr(400, response.text);
          }
        },
        error: (error) => {
          this.commonService.setToastr(500, "Error uploading file");
        },
      });
    }
  }

  public downloadFile(filePath: string, index: number) {
    console.log(`Downloading file ${index + 1}:`, filePath);

    if (filePath) {
      const fileName = filePath.split('/').pop() || `downloaded-file-${index + 1}`;
      const anchor = document.createElement("a");
      anchor.href = filePath;
      anchor.target = "_blank";
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } else {
      this.commonService.setToastr(500, `File path is missing for file ${index + 1}!`);
    }
  }

  public saveDocuments(documentDownload: FormGroup, index: number) {
    this.submitted = true;
    this.documentDownloadForm.markAllAsTouched();
    if (this.documentDownloadForm.valid) {
      const document = documentDownload.value as DocumentDownload;
      this.separationService.createDocumentDownload(document).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            this.documentDownloads.at(index).patchValue({ id: parseInt(response.payload) });
            if (this.selectedFile) {
              this.uploadFile(parseInt(response.payload));
            } 
            this.commonService.setToastr(200, `Document has been uploaded successfully!`);
          } else {
            this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
          }
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.setToastr(500, error?.error?.name || error.error);
        },
      });
    }
  }
}